import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema } from "@/lib/schema";
import type { RecommendFact, RecommendSection } from "@/content/recommend-corpus/types";

export const Route = createFileRoute("/recommend/$hub/$topic")({
  loader: async ({ params }) => {
    const { getRecommendHub } = await import("@/content/recommend-corpus/hubs");
    const { loadHubTopics, loadTopicKeywords } = await import("@/content/recommend-corpus/hub-lazy");
    const hub = getRecommendHub(params.hub);
    if (!hub) throw notFound();
    const topics = await loadHubTopics(hub.slug);
    const topic = topics.find((t) => t.slug === params.topic);
    if (!topic) throw notFound();
    return { hub, topic, keywords: await loadTopicKeywords(topic, 40) };
  },
  component: RecommendTopicPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบหน้าแนะนำบริการ" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { hub, topic, keywords } = loaderData;
    const path = `/recommend/${params.hub}/${params.topic}`;
    return {
      ...pageHeadForProvince({
        title: topic.metaTitle,
        description: topic.metaDesc,
        path,
        type: "article",
        keywords: keywords.slice(0, 25),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "แนะนำบริการ", url: `${site}/recommend` },
              { name: hub.titleTh, url: `${site}/recommend/${params.hub}` },
              { name: topic.titleTh, url: `${site}${path}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(topic.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: topic.titleTh,
            step: topic.howto.map((s: { name: string; text: string }, i: number) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.name,
              text: s.text,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: [".speakable-summary", ".speakable-faq"],
            },
          }),
        },
      ],
    };
  },
});

function RecommendTopicPage() {
  const { hub, topic, keywords } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/recommend" className="hover:underline">แนะนำบริการ</a> <span>/</span>{" "}
          <a href={`/recommend/${hub.slug}`} className="hover:underline">{hub.titleTh}</a> <span>/</span>{" "}
          <span>{topic.titleTh}</span>
        </nav>
        <h1 className="mb-4 text-3xl font-bold md:text-4xl">{topic.titleTh}</h1>
        <p className="speakable-summary mb-8 rounded-lg border bg-muted/30 p-5 text-sm leading-relaxed">{topic.quickAnswer}</p>

        <section className="mb-10">
          <h2 className="mb-3 text-2xl font-bold">ข้อเท็จจริงสำคัญ</h2>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <tbody>
                {topic.facts.map((f: RecommendFact) => (
                  <tr key={f.label} className="border-b last:border-0">
                    <th scope="row" className="w-1/3 bg-muted/30 px-4 py-3 text-left font-medium align-top">{f.label}</th>
                    <td className="px-4 py-3 text-muted-foreground">{f.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {topic.sections.map((s: RecommendSection) => (
          <section key={s.h2} className="mb-10">
            <h2 className="mb-3 text-2xl font-bold">{s.h2}</h2>
            {s.body.map((p: string, i: number) => (
              <p key={i} className="mb-3 leading-relaxed text-muted-foreground">{p}</p>
            ))}
            {s.bullets && (
              <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-muted-foreground">
                {s.bullets.map((b: string) => <li key={b}>{b}</li>)}
              </ul>
            )}
          </section>
        ))}

        <section className="mb-10">
          <h2 className="mb-3 text-2xl font-bold">ขั้นตอนดำเนินการ</h2>
          <ol className="space-y-3">
            {topic.howto.map((s: { name: string; text: string }, i: number) => (
              <li key={s.name} className="rounded-lg border bg-card p-4">
                <p className="font-semibold">ขั้นที่ {i + 1}: {s.name}</p>
                <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-2xl font-bold">คำถามที่พบบ่อย</h2>
          <div className="space-y-3">
            {topic.faqs.map((f: { q: string; a: string }, i: number) => (
              <details key={f.q} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="speakable-faq mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-2xl font-bold">แหล่งอ้างอิงราชการ</h2>
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {topic.citations.map((c: { label: string; note: string }) => (
              <li key={c.label}><strong className="text-foreground">{c.label}</strong> — {c.note}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-2xl font-bold">อ่านต่อ</h2>
          <ul className="list-inside list-disc space-y-1 text-sm">
            {topic.related.map((r: { label: string; href: string }) => (
              <li key={r.href}><a href={r.href} className="text-primary underline">{r.label}</a></li>
            ))}
          </ul>
        </section>

        {keywords.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-3 text-2xl font-bold">คำค้นที่หน้านี้ตอบ</h2>
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {keywords.map((k: string) => (
                <span key={k} className="rounded-full border bg-muted/40 px-3 py-1">{k}</span>
              ))}
            </div>
          </section>
        )}

        <section className="rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">ให้ทีมงานตรวจเอกสารก่อนเริ่มงาน</h2>
          <p className="mb-4 text-sm text-muted-foreground">ส่งรายการเอกสารและปลายทางที่จะยื่น ทีมงานจะแจ้งลำดับขั้นและระยะเวลาที่ต้องใช้</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">ติดต่อทีมงาน →</a>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
